/**
 * SOLID Review
 * S — Strong. One concern: the payment's success screen — who is signed in, the credits the balance poll found, and the way back to
 *     the game.
 * O — Good. Another line of the receipt is the view's.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The screen's snapshot and its one action.
 * D — Strong. One use case, the client view, the request contexts, the balance poll's controller.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions } from './Reactions';
import type { PaymentBalancePoller, IStateUsePaymentBalancePoll } from './PaymentBalancePoller';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const PLAY_NOW: TClientUseCaseInput<'navigate'> = { screen: 'GAME' };

export interface IStatePaymentSuccess {
  readonly registered: boolean;
  readonly playerId: string | null;
  /** The balance the poll found — null until the first read lands. */
  readonly creditsGranted: number | null;
}

/**
 * THE PAYMENT'S SUCCESS SCREEN (frontend-framework-oo P5, 2026-10-30, §28.298) — `components/payment/PaymentSuccessScreen.tsx`'s reads,
 * its action, and the balance poll its `usePaymentBalancePoll` bound: the poll's controller is composed here and its reaction runs in
 * this model's list (the binding is gone, as AppRoot's hooks' went, §28.295). The balance read sends the token inside Infrastructure and
 * the Application keeps what it reads (strict-ports FE4c).
 */
export class PaymentSuccessViewModel implements IServiceViewModel<IStatePaymentSuccess> {
  private readonly reactions: Reactions<IStatePaymentSuccess>;
  private shown: {
    readonly registered: boolean; readonly playerId: string | null; readonly poll: IStateUsePaymentBalancePoll; readonly screen: IStatePaymentSuccess;
  } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'navigate'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
    private readonly poller: PaymentBalancePoller,
  ) {
    this.reactions = new Reactions<IStatePaymentSuccess>(poller.reactionsOver((s: IStatePaymentSuccess) => [s.registered, s.playerId] as const));
  }

  readonly playNow = (): void => {
    void this.useCases.navigate(this.requests.context(), PLAY_NOW);
  };

  getSnapshot(): IStatePaymentSuccess {
    const { account } = this.view.getSnapshot().session;
    const registered = account.kind === 'registered';
    const { playerId } = account;
    const poll = this.poller.getSnapshot();
    const shown = this.shown;
    if (shown !== null && shown.registered === registered && shown.playerId === playerId && shown.poll === poll) return shown.screen;
    const screen: IStatePaymentSuccess = { registered, playerId, creditsGranted: poll.creditsGranted };
    this.shown = { registered, playerId, poll, screen };
    return screen;
  }

  subscribe(listener: () => void): () => void {
    const leaveView = this.view.subscribe(listener);
    const leavePoll = this.poller.subscribe(listener);
    return () => {
      leaveView();
      leavePoll();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStatePaymentSuccess): void {
    this.reactions.stopChanged(rendered);
  }

  commit(_inputs: readonly unknown[], rendered: IStatePaymentSuccess): void {
    this.reactions.startChanged(rendered);
  }
}
